import React, { useState, useEffect } from 'react';
import axios from 'axios';

interface Credential {
  id: number;
  service: string;
  credentials: any;
  is_active: boolean;
  updatedBy?: {
    name: string;
    email: string;
  };
  updated_at: string;
}

const AppCredentials: React.FC = () => {
  const [credentials, setCredentials] = useState<Credential[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingService, setEditingService] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<any>({});
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState<{ success: boolean; message: string; details?: string } | null>(null);
  const [saving, setSaving] = useState(false);

  // Show/hide password state
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/admin/app-credentials');
      if (response.data.success) {
        setCredentials(response.data.data.credentials);
      }
    } catch (error) {
      console.error('Error loading credentials:', error);
    } finally {
      setLoading(false);
    }
  };

  const getServiceIcon = (service: string) => {
    const icons: Record<string, string> = {
      aws_s3: 'bx-cloud-upload',
      smtp: 'bx-envelope',
      google_oauth: 'bxl-google',
      facebook_oauth: 'bxl-facebook-circle',
      firebase_fcm: 'bxl-firebase',
      recaptcha: 'bx-shield-check'
    };
    return icons[service] || 'bx-cog';
  };

  const getServiceName = (service: string) => {
    const names: Record<string, string> = {
      aws_s3: 'AWS S3 Storage',
      smtp: 'SMTP / Email Service',
      google_oauth: 'Google OAuth',
      facebook_oauth: 'Facebook OAuth',
      firebase_fcm: 'Firebase Cloud Messaging',
      recaptcha: 'Google reCAPTCHA'
    };
    return names[service] || service;
  };

  const isConfigured = (cred: Credential) => {
    const creds = cred.credentials;
    if (!creds) return false;

    switch (cred.service) {
      case 'aws_s3':
        return !!(creds.bucket_name && creds.region);
      case 'smtp':
        return !!(creds.host && creds.port && creds.user && creds.pass);
      case 'google_oauth':
        return !!creds.client_id;
      case 'facebook_oauth':
        return !!(creds.app_id && creds.app_secret);
      case 'firebase_fcm':
        return !!(creds.project_id && creds.client_email && creds.private_key);
      case 'recaptcha':
        return !!(creds.site_key && creds.secret_key);
      default:
        return false;
    }
  };

  const handleEdit = (service: string, creds: any) => {
    setEditingService(service);
    setEditForm({ ...creds });
    setShowEditModal(true);
    setTestResult(null);
    setShowSecrets({});
  };

  const handleTestConnection = async () => {
    if (!editingService) return;

    setTesting(true);
    setTestResult(null);

    try {
      const response = await axios.post(`/admin/app-credentials/${editingService}/test`, {
        credentials: editForm
      });

      setTestResult({
        success: response.data.success,
        message: response.data.message,
        details: response.data.details
      });
    } catch (error: any) {
      setTestResult({
        success: false,
        message: error.response?.data?.message || 'Connection test failed',
        details: error.response?.data?.details
      });
    } finally {
      setTesting(false);
    }
  };

  const handleSave = async () => {
    if (!editingService) return;

    setSaving(true);
    try {
      const response = await axios.put(`/admin/app-credentials/${editingService}`, {
        credentials: editForm
      });

      if (response.data.success) {
        alert('Credentials updated successfully! Service reloaded.');
        loadCredentials();
        setShowEditModal(false);
        setEditingService(null);
        setEditForm({});
        setTestResult(null);
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to update credentials');
    } finally {
      setSaving(false);
    }
  };

  const toggleSecret = (key: string) => {
    setShowSecrets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const renderCredentialValue = (key: string, value: string, isSensitive: boolean) => {
    if (!value) return <span className="text-muted">Not set</span>;

    if (isSensitive) {
      return (
        <span className="font-monospace">
          {value.includes('••••') ? value : '••••••••'}
        </span>
      );
    }

    return <span>{value}</span>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const renderServiceCard = (cred: Credential) => {
    const configured = isConfigured(cred);
    const creds = cred.credentials;

    return (
      <div key={cred.service} className="col-lg-6 mb-4">
        <div className="card h-100">
          <div className="card-body">
            {/* Header */}
            <div className="d-flex justify-content-between align-items-start mb-3">
              <div className="d-flex align-items-center">
                <div className="avatar-sm flex-shrink-0 me-3">
                  <span className={`avatar-title bg-${configured ? 'success' : 'secondary'} bg-soft rounded`}>
                    <i className={`bx ${getServiceIcon(cred.service)} font-size-24 text-${configured ? 'success' : 'secondary'}`}></i>
                  </span>
                </div>
                <div>
                  <h5 className="mb-1">{getServiceName(cred.service)}</h5>
                  <span className={`badge bg-${configured ? 'success' : 'warning'}`}>
                    {configured ? 'Configured' : 'Not Set'}
                  </span>
                </div>
              </div>
            </div>

            {/* Credentials Display */}
            <div className="mb-3">
              {cred.service === 'aws_s3' && (
                <>
                  <div className="mb-2">
                    <small className="text-muted">Region:</small> {renderCredentialValue('region', creds.region, false)}
                  </div>
                  <div className="mb-2">
                    <small className="text-muted">Bucket:</small> {renderCredentialValue('bucket_name', creds.bucket_name, false)}
                  </div>
                  <div className="mb-2">
                    <small className="text-muted">Access Key:</small> {renderCredentialValue('access_key_id', creds.access_key_id, true)}
                  </div>
                  <div className="mb-2">
                    <small className="text-muted">Secret Key:</small> {renderCredentialValue('secret_access_key', creds.secret_access_key, true)}
                  </div>
                </>
              )}

              {cred.service === 'smtp' && (
                <>
                  <div className="mb-2">
                    <small className="text-muted">Host:</small> {renderCredentialValue('host', creds.host, false)}
                  </div>
                  <div className="mb-2">
                    <small className="text-muted">Port:</small> {renderCredentialValue('port', creds.port, false)}
                  </div>
                  <div className="mb-2">
                    <small className="text-muted">User:</small> {renderCredentialValue('user', creds.user, false)}
                  </div>
                  <div className="mb-2">
                    <small className="text-muted">Password:</small> {renderCredentialValue('pass', creds.pass, true)}
                  </div>
                  <div className="mb-2">
                    <small className="text-muted">From Email:</small> {renderCredentialValue('from_email', creds.from_email, false)}
                  </div>
                </>
              )}

              {cred.service === 'google_oauth' && (
                <div className="mb-2">
                  <small className="text-muted">Client ID:</small> {renderCredentialValue('client_id', creds.client_id, false)}
                </div>
              )}

              {cred.service === 'facebook_oauth' && (
                <>
                  <div className="mb-2">
                    <small className="text-muted">App ID:</small> {renderCredentialValue('app_id', creds.app_id, false)}
                  </div>
                  <div className="mb-2">
                    <small className="text-muted">App Secret:</small> {renderCredentialValue('app_secret', creds.app_secret, true)}
                  </div>
                </>
              )}

              {cred.service === 'firebase_fcm' && (
                <>
                  <div className="mb-2">
                    <small className="text-muted">Project ID:</small> {renderCredentialValue('project_id', creds.project_id, false)}
                  </div>
                  <div className="mb-2">
                    <small className="text-muted">Client Email:</small> {renderCredentialValue('client_email', creds.client_email, false)}
                  </div>
                  <div className="mb-2">
                    <small className="text-muted">Private Key:</small> {renderCredentialValue('private_key', creds.private_key ? '••••••••' : '', true)}
                  </div>
                </>
              )}

              {cred.service === 'recaptcha' && (
                <>
                  <div className="mb-2">
                    <small className="text-muted">Site Key:</small> {renderCredentialValue('site_key', creds.site_key, false)}
                  </div>
                  <div className="mb-2">
                    <small className="text-muted">Secret Key:</small> {renderCredentialValue('secret_key', creds.secret_key ? '••••••••' : '', true)}
                  </div>
                  <div className="mb-2">
                    <small className="text-muted">Status:</small> <span className={`badge bg-${creds.is_enabled ? 'success' : 'secondary'}`}>{creds.is_enabled ? 'Enabled' : 'Disabled'}</span>
                  </div>
                </>
              )}
            </div>

            {/* Footer */}
            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">
                {cred.updatedBy ? `Updated by ${cred.updatedBy.name}` : 'Not updated yet'}
                <br />
                {cred.updated_at && formatDate(cred.updated_at)}
              </small>
              <button
                className="btn btn-sm btn-primary"
                onClick={() => handleEdit(cred.service, cred.credentials)}
              >
                <i className="bx bx-edit me-1"></i>
                Edit
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderEditModal = () => {
    if (!editingService) return null;

    return (
      <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                <i className={`bx ${getServiceIcon(editingService)} me-2`}></i>
                Edit {getServiceName(editingService)}
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingService(null);
                  setEditForm({});
                  setTestResult(null);
                }}
              ></button>
            </div>
            <div className="modal-body">
              {/* AWS S3 Form */}
              {editingService === 'aws_s3' && (
                <>
                  <div className="mb-3">
                    <label className="form-label">Access Key ID <small className="text-muted">(optional - leave blank to use IAM role)</small></label>
                    <input
                      type="text"
                      className="form-control font-monospace"
                      value={editForm.access_key_id || ''}
                      onChange={(e) => setEditForm({ ...editForm, access_key_id: e.target.value })}
                      placeholder="AKIAIOSFODNN7EXAMPLE"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Secret Access Key <small className="text-muted">(optional - leave blank to use IAM role)</small></label>
                    <div className="input-group">
                      <input
                        type={showSecrets.secret ? 'text' : 'password'}
                        className="form-control font-monospace"
                        value={editForm.secret_access_key || ''}
                        onChange={(e) => setEditForm({ ...editForm, secret_access_key: e.target.value })}
                        placeholder="wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY"
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => toggleSecret('secret')}
                      >
                        <i className={`bx ${showSecrets.secret ? 'bx-hide' : 'bx-show'}`}></i>
                      </button>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Region</label>
                      <select
                        className="form-select"
                        value={editForm.region || 'ap-south-1'}
                        onChange={(e) => setEditForm({ ...editForm, region: e.target.value })}
                      >
                        <option value="ap-south-1">Asia Pacific (Mumbai) - ap-south-1</option>
                        <option value="us-east-1">US East (N. Virginia) - us-east-1</option>
                        <option value="us-west-2">US West (Oregon) - us-west-2</option>
                        <option value="eu-west-1">Europe (Ireland) - eu-west-1</option>
                        <option value="ap-southeast-1">Asia Pacific (Singapore) - ap-southeast-1</option>
                      </select>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label">Bucket Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editForm.bucket_name || ''}
                        onChange={(e) => setEditForm({ ...editForm, bucket_name: e.target.value })}
                        placeholder="nyvro-s3-bucket"
                      />
                    </div>
                  </div>
                </>
              )}

              {/* SMTP Form */}
              {editingService === 'smtp' && (
                <>
                  <div className="row">
                    <div className="col-md-8 mb-3">
                      <label className="form-label">Host</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editForm.host || ''}
                        onChange={(e) => setEditForm({ ...editForm, host: e.target.value })}
                        placeholder="smtp.gmail.com"
                      />
                    </div>
                    <div className="col-md-4 mb-3">
                      <label className="form-label">Port</label>
                      <select
                        className="form-select"
                        value={editForm.port || '587'}
                        onChange={(e) => setEditForm({ ...editForm, port: e.target.value })}
                      >
                        <option value="587">587 (TLS)</option>
                        <option value="465">465 (SSL)</option>
                        <option value="25">25 (Plain)</option>
                      </select>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Username / Email</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.user || ''}
                      onChange={(e) => setEditForm({ ...editForm, user: e.target.value })}
                      placeholder="noreply@yourapp.com"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Password / App Password</label>
                    <div className="input-group">
                      <input
                        type={showSecrets.password ? 'text' : 'password'}
                        className="form-control"
                        value={editForm.pass || ''}
                        onChange={(e) => setEditForm({ ...editForm, pass: e.target.value })}
                        placeholder="••••••••••••"
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => toggleSecret('password')}
                      >
                        <i className={`bx ${showSecrets.password ? 'bx-hide' : 'bx-show'}`}></i>
                      </button>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">From Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={editForm.from_email || ''}
                      onChange={(e) => setEditForm({ ...editForm, from_email: e.target.value })}
                      placeholder="noreply@yourapp.com"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">From Name</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.from_name || 'Nyvro'}
                      onChange={(e) => setEditForm({ ...editForm, from_name: e.target.value })}
                      placeholder="Nyvro"
                    />
                  </div>
                </>
              )}

              {/* Google OAuth Form */}
              {editingService === 'google_oauth' && (
                <div className="mb-3">
                  <label className="form-label">Google Client ID</label>
                  <input
                    type="text"
                    className="form-control font-monospace"
                    value={editForm.client_id || ''}
                    onChange={(e) => setEditForm({ ...editForm, client_id: e.target.value })}
                    placeholder="123456789-abcdefghijklmnop.apps.googleusercontent.com"
                  />
                  <small className="text-muted">Get this from Google Cloud Console</small>
                </div>
              )}

              {/* Facebook OAuth Form */}
              {editingService === 'facebook_oauth' && (
                <>
                  <div className="mb-3">
                    <label className="form-label">Facebook App ID</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.app_id || ''}
                      onChange={(e) => setEditForm({ ...editForm, app_id: e.target.value })}
                      placeholder="1234567890123456"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">App Secret</label>
                    <div className="input-group">
                      <input
                        type={showSecrets.fbsecret ? 'text' : 'password'}
                        className="form-control font-monospace"
                        value={editForm.app_secret || ''}
                        onChange={(e) => setEditForm({ ...editForm, app_secret: e.target.value })}
                        placeholder="••••••••••••••••••••••••••••••••"
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => toggleSecret('fbsecret')}
                      >
                        <i className={`bx ${showSecrets.fbsecret ? 'bx-hide' : 'bx-show'}`}></i>
                      </button>
                    </div>
                  </div>
                </>
              )}

              {/* Firebase FCM Form */}
              {editingService === 'firebase_fcm' && (
                <>
                  <div className="mb-3">
                    <label className="form-label">Project ID</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editForm.project_id || ''}
                      onChange={(e) => setEditForm({ ...editForm, project_id: e.target.value })}
                      placeholder="your-project-id"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Client Email</label>
                    <input
                      type="email"
                      className="form-control"
                      value={editForm.client_email || ''}
                      onChange={(e) => setEditForm({ ...editForm, client_email: e.target.value })}
                      placeholder="firebase-adminsdk-xxxxx@your-project.iam.gserviceaccount.com"
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Private Key</label>
                    <textarea
                      className="form-control font-monospace"
                      rows={10}
                      value={editForm.private_key || ''}
                      onChange={(e) => setEditForm({ ...editForm, private_key: e.target.value })}
                      placeholder="-----BEGIN PRIVATE KEY-----&#10;MIIEvg...&#10;-----END PRIVATE KEY-----"
                      style={{ fontSize: '12px' }}
                    />
                    <small className="text-muted">Paste the Firebase private key (including BEGIN and END lines)</small>
                  </div>
                </>
              )}

              {/* reCAPTCHA Form */}
              {editingService === 'recaptcha' && (
                <>
                  <div className="mb-3">
                    <label className="form-label">Site Key (Public)</label>
                    <input
                      type="text"
                      className="form-control font-monospace"
                      value={editForm.site_key || ''}
                      onChange={(e) => setEditForm({ ...editForm, site_key: e.target.value })}
                      placeholder="6Lcxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                    />
                    <small className="text-muted">Get this from Google reCAPTCHA Admin Console</small>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Secret Key (Private)</label>
                    <div className="input-group">
                      <input
                        type={showSecrets.recaptcha ? 'text' : 'password'}
                        className="form-control font-monospace"
                        value={editForm.secret_key || ''}
                        onChange={(e) => setEditForm({ ...editForm, secret_key: e.target.value })}
                        placeholder="6Lcxxxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                      />
                      <button
                        className="btn btn-outline-secondary"
                        type="button"
                        onClick={() => toggleSecret('recaptcha')}
                      >
                        <i className={`bx ${showSecrets.recaptcha ? 'bx-hide' : 'bx-show'}`}></i>
                      </button>
                    </div>
                    <small className="text-muted">Keep this secret - never expose it publicly</small>
                  </div>
                  <div className="mb-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={editForm.is_enabled === true}
                        onChange={(e) => setEditForm({ ...editForm, is_enabled: e.target.checked })}
                        id="recaptchaEnabled"
                      />
                      <label className="form-check-label" htmlFor="recaptchaEnabled">
                        Enable reCAPTCHA for website forms
                      </label>
                    </div>
                  </div>
                </>
              )}

              {/* Test Result */}
              {testResult && (
                <div className={`alert alert-${testResult.success ? 'success' : 'danger'} mt-3`}>
                  <strong>{testResult.success ? '✅' : '❌'} {testResult.message}</strong>
                  {testResult.details && (
                    <div className="mt-1 small">{testResult.details}</div>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowEditModal(false);
                  setEditingService(null);
                  setEditForm({});
                  setTestResult(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-info"
                onClick={handleTestConnection}
                disabled={testing}
              >
                {testing ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1"></span>
                    Testing...
                  </>
                ) : (
                  <>
                    <i className="bx bx-test-tube me-1"></i>
                    Test Connection
                  </>
                )}
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-1"></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <i className="bx bx-save me-1"></i>
                    Save & Apply
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <div className="row mb-4">
        <div className="col-12">
          <h4 className="mb-1 font-size-24 fw-semibold" style={{ color: '#495057' }}>App Credentials</h4>
          <p className="text-muted mb-0">Manage external service credentials and API keys</p>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      ) : (
        <div className="row">
          {credentials.map(cred => renderServiceCard(cred))}
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && renderEditModal()}
    </div>
  );
};

export default AppCredentials;

